import useLocalStorage from 'hooks/useLocalStorage';
import { createContext, FC, useContext, useState, useMemo } from 'react';
import { BarcodeLabelSchemaLite } from 'types/clientV2/stock';
import {
  useCreateBarcodeLabelMutation,
  useVoidBarcodeLabelsMutation,
} from 'store/slices/clientV2/barcodeLabels';
import { PackagingOptions, StockLotCRUDSchema } from 'types/inventory';
import { ShipmentToReceive } from 'views/warehouse/hooks/useGetShipmentsToReceive';
import {
  warehouseDefaults,
  warehouseStorageName,
} from 'types/warehouseSettings';
import { PurchaseEventLite, PurchaseLite } from 'types/purchasing';
import { OrgSupplier } from 'types/suppliers';
import MobileLoader from 'ui-component/mobile/MobileLoader';
import useSnackbar from 'hooks/useSnackbar';
import usePrinting from 'hooks/usePrinting';
import _ from 'lodash';
import { useConfirm } from 'material-ui-confirm';
import {
  cancellationButtonProps,
  confirmationButtonProps,
} from 'constants/appConstants';
import { useCompleteShipmentIngestMutation } from 'store/slices/apiV1/inventory';
import { useScannerContext } from 'contexts/ScannerContext';
import { useCreateReceiveShipmentLineMutation } from 'store/slices/clientV2/stockLots';
import { useWarehouseWorkstationContext } from 'contexts/WarehouseWorkstationContext';

export enum receiveModes {
  supplier = 'supplier',
  shipments = 'shipments',
}

export type PendingStockLotReceive = {
  stockLot: StockLotCRUDSchema;
  packageType: PackagingOptions;
  quant: number;
};

type ReceiveV2ContextType = {
  mode: receiveModes;
  setMode: (mode: receiveModes) => void;
  selectedShipment: ShipmentToReceive | null;
  selectedShipmentId: string | null;
  setSelectedShipmentId: (shipmentId: string | null) => void;
  orgSupplier: OrgSupplier | null;
  setOrgSupplier: (orgSupplier: OrgSupplier | null) => void;
  purchaseEvent: PurchaseEventLite | null;
  setPurchaseEvent: (purchaseEvent: PurchaseEventLite | null) => void;
  selectedPurchase: PurchaseLite | null;
  setSelectedPurchase: (purchase: PurchaseLite | null) => void;
  handleReset: () => void;
  createdBarcodeLabels: BarcodeLabelSchemaLite[];
  handleCreateBarcodeLabel: (
    packageType: PackagingOptions,
    onBarcodeLabelCreated?: (barcodeLabel: BarcodeLabelSchemaLite) => void
  ) => Promise<void>;
  isCreatingBarcodeLabel: boolean;
  handleCompleteIngest: () => Promise<void>;
  scannedStockLot: StockLotCRUDSchema | null;
  setScannedStockLot: (stockLot: StockLotCRUDSchema | null) => void;
  handleStockLotReceive: (data: {
    packageType: PackagingOptions;
    quant: number;
  }) => void;
  pendingStockLotReceive: PendingStockLotReceive | null;
  setPendingStockLotReceive: (data: PendingStockLotReceive | null) => void;
  handleFinalStockLotReceive: () => Promise<void>;
  isLoadingCreateAndReceiveShipmentLine: boolean;
  setShipmentsToReceive: (shipments: ShipmentToReceive[]) => void;
  voidBarcodeLabelById: (barcodeLabelId: string) => Promise<void>;
};

const ReceiveV2Context = createContext<ReceiveV2ContextType | undefined>(
  undefined
);

const generateLabelCode = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = 'Z';
  for (let i = 0; i < 16; i += 1) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

export const ReceiveV2Provider: FC = ({ children }) => {
  const [settings] = useLocalStorage(warehouseStorageName, warehouseDefaults);
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();
  const confirm = useConfirm();
  const [mode, setMode] = useState<receiveModes>(receiveModes.supplier);
  const [selectedShipmentId, setSelectedShipmentId] = useState<string | null>(
    null
  );
  const [orgSupplier, setOrgSupplier] = useState<OrgSupplier | null>(null);
  const [purchaseEvent, setPurchaseEvent] = useState<PurchaseEventLite | null>(
    null
  );
  const [selectedPurchase, setSelectedPurchase] = useState<PurchaseLite | null>(
    null
  );
  const [createdBarcodeLabels, setCreatedBarcodeLabels] = useState<
    BarcodeLabelSchemaLite[]
  >([]);
  const [scannedStockLot, setScannedStockLot] =
    useState<StockLotCRUDSchema | null>(null);
  const [pendingStockLotReceive, setPendingStockLotReceive] =
    useState<PendingStockLotReceive | null>(null);

  const [shipmentsToReceive, setShipmentsToReceive] = useState<
    ShipmentToReceive[]
  >([]);

  const selectedShipment = useMemo(() => {
    if (!selectedShipmentId || !shipmentsToReceive.length) return null;
    return (
      shipmentsToReceive.find(
        (shipment) => shipment.id === selectedShipmentId
      ) || null
    );
  }, [selectedShipmentId, shipmentsToReceive]);

  const [createBarcodeLabel, { isLoading: isCreatingBarcodeLabel }] =
    useCreateBarcodeLabelMutation();

  const [voidBarcodeLabels] = useVoidBarcodeLabelsMutation();

  const [
    createAndReceiveShipmentLine,
    { isLoading: isLoadingCreateAndReceiveShipmentLine },
  ] = useCreateReceiveShipmentLineMutation();

  const { handleLocalPrintLabelData } = usePrinting({});

  const { targetLocation } = useWarehouseWorkstationContext();

  const handleCreateBarcodeLabel = async (packageType: PackagingOptions) => {
    const createPayload = {
      package: packageType,
      labelCode: '',
      stockLocation: targetLocation?.id || settings?.currentWorkstation?.id,
      purchase: selectedPurchase?.id || null,
      purchaseEvent: purchaseEvent?.id || null,
      shipment: selectedShipment?.id || null,
    };

    let attempts = 0;
    const maxAttempts = 5;
    let success = false;
    let barcodeLabelId = '';

    while (!success && attempts < maxAttempts) {
      try {
        createPayload.labelCode = generateLabelCode();
        // we ignore the es-lint no-await-in-loop error in this case because we specifically need to try the POST multiple times in the very rate case of a labelCode collision that causes the backend to throw a unique constraint error
        // eslint-disable-next-line no-await-in-loop
        const response = await createBarcodeLabel(createPayload);
        if (_.get(response, ['data', 'createdIds', 0])) {
          success = true;
          barcodeLabelId = _.get(response, ['data', 'createdIds', 0]);
        }
      } catch (error) {
        attempts += 1;
      }
    }

    if (success) {
      const newBarcodeLabel = {
        ...createPayload,
        id: barcodeLabelId,
        needsReview: false,
        facility: settings?.currentWorkstation?.facility,
        stockLot: null,
        createdAt: new Date().toISOString(),
        voidedAt: null,
        voidedBy: null,
      };
      setCreatedBarcodeLabels((prev) => [...prev, newBarcodeLabel]);
      handleLocalPrintLabelData({
        recordType: 'stockLot',
        labelTemplateName: 'Stock Lot',
        data: {
          printedPart: newBarcodeLabel.labelCode.slice(0, 12),
          lotId: newBarcodeLabel.labelCode.slice(-5),
        },
      });
      dispatchSuccessSnackbar('Label created');
    } else {
      dispatchErrorSnackbar('Failed to create barcode label');
    }
  };

  const handleReset = () => {
    setMode(receiveModes.supplier);
    setSelectedShipmentId(null);
    setOrgSupplier(null);
    setPurchaseEvent(null);
    setSelectedPurchase(null);
    setCreatedBarcodeLabels([]);
    setPendingStockLotReceive(null);
  };

  const handleScannedStockLot = async (stockLot: StockLotCRUDSchema) => {
    if (!selectedShipment) {
      dispatchErrorSnackbar('No shipment selected');
      return;
    }
    if (!targetLocation?.id && !settings?.currentWorkstation?.id) {
      dispatchErrorSnackbar('No target location or workstation selected');
      return;
    }

    // Show dialog for shipment receives to confirm package type and quantity
    setScannedStockLot(stockLot);
  };

  const handleStockLotReceive = (data: {
    packageType: PackagingOptions;
    quant: number;
  }) => {
    if (!scannedStockLot || !selectedShipment) {
      dispatchErrorSnackbar('Invalid state for stock lot receive');
      return;
    }

    setPendingStockLotReceive({
      stockLot: scannedStockLot,
      packageType: data.packageType,
      quant: data.quant,
    });
    setScannedStockLot(null);
  };

  const handleFinalStockLotReceive = async () => {
    if (!pendingStockLotReceive || !selectedShipment) {
      dispatchErrorSnackbar('Invalid state for final stock lot receive');
      return;
    }

    try {
      await createAndReceiveShipmentLine({
        shipment: selectedShipment.id,
        stockLocation:
          targetLocation?.id || settings?.currentWorkstation?.id || '',
        stockLot: pendingStockLotReceive.stockLot.id,
        quant: pendingStockLotReceive.quant,
      }).unwrap();

      const newBarcodeLabel = {
        id: pendingStockLotReceive.stockLot.id,
        facility: settings?.currentWorkstation?.facility,
        labelCode: `${pendingStockLotReceive.stockLot.printedPart}${pendingStockLotReceive.stockLot.lotId}`,
        package: pendingStockLotReceive.packageType,
        stockLocation:
          targetLocation?.id || settings?.currentWorkstation?.id || '',
        shipment: selectedShipment.id,
        purchase: selectedPurchase?.id || null,
        purchaseEvent: purchaseEvent?.id || null,
        createdAt: pendingStockLotReceive.stockLot.createdAt,
        stockLot: pendingStockLotReceive.stockLot.id,
        needsReview: false,
        voidedAt: null,
        voidedBy: null,
      };

      setCreatedBarcodeLabels((prev) => [...prev, newBarcodeLabel]);
      setPendingStockLotReceive(null);
      dispatchSuccessSnackbar('Stock lot received');
    } catch (err) {
      dispatchErrorSnackbar('Error creating and receiving shipment line');
    }
  };

  const voidBarcodeLabelById = async (barcodeLabelId: string) => {
    try {
      await voidBarcodeLabels({ ids: [barcodeLabelId], voided: true }).unwrap();
      setCreatedBarcodeLabels((prev) =>
        prev.map((label) =>
          label.id === barcodeLabelId
            ? {
                ...label,
                voidedAt: new Date().toISOString(),
                voidedBy: null,
              }
            : label
        )
      );
      dispatchSuccessSnackbar('Label voided');
    } catch (err) {
      dispatchErrorSnackbar('Failed to void barcode label');
    }
  };

  useScannerContext({
    handleScannedStockLot,
  });

  const [
    completeShipmentIngest,
    { isLoading: isLoadingCompleteShipmentIngest },
  ] = useCompleteShipmentIngestMutation();

  const handleCompleteIngest = async () => {
    confirm({
      title: 'Package Empty?',
      description: 'Are you sure that this package is completely empty?',
      confirmationText: "Yes, I'm Sure",
      cancellationButtonProps,
      confirmationButtonProps,
    }).then(async () => {
      if (selectedShipment) {
        try {
          await completeShipmentIngest(selectedShipment.id).unwrap();
          dispatchSuccessSnackbar('Shipment completed');
          handleReset();
        } catch (err) {
          dispatchErrorSnackbar('Error completing shipment ingest');
        }
      }
      if (selectedPurchase) {
        dispatchSuccessSnackbar('Package completed');
        handleReset();
      }
    });
  };

  return (
    <ReceiveV2Context.Provider
      value={{
        mode,
        setMode,
        selectedShipment,
        selectedShipmentId,
        setSelectedShipmentId,
        orgSupplier,
        setOrgSupplier,
        purchaseEvent,
        setPurchaseEvent,
        selectedPurchase,
        setSelectedPurchase,
        handleReset,
        createdBarcodeLabels,
        handleCreateBarcodeLabel,
        isCreatingBarcodeLabel,
        handleCompleteIngest,
        scannedStockLot,
        setScannedStockLot,
        handleStockLotReceive,
        pendingStockLotReceive,
        setPendingStockLotReceive,
        handleFinalStockLotReceive,
        isLoadingCreateAndReceiveShipmentLine,
        setShipmentsToReceive,
        voidBarcodeLabelById,
      }}
    >
      {children}
      {(isCreatingBarcodeLabel ||
        isLoadingCompleteShipmentIngest ||
        isLoadingCreateAndReceiveShipmentLine) && <MobileLoader />}
    </ReceiveV2Context.Provider>
  );
};

export const useReceiveV2Context = () => {
  const context = useContext(ReceiveV2Context);
  if (!context) {
    throw new Error(
      'useReceiveV2Context must be used within a ReceiveV2Provider'
    );
  }
  return context;
};
